import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import { api, API, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
const UNLOCK_KEY = "cbd_unlocked_assignments";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function getUnlocked() {
  try {
    const v = JSON.parse(localStorage.getItem(UNLOCK_KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

export default function Assignments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(getUnlocked);
  const [price, setPrice] = useState(49);

  useEffect(() => {
    api.get("/assignments").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/config").then((r) => setPrice(Number(r.data.assignment_price_inr) || 49)).catch(() => {});
  }, []);

  const markUnlocked = (id) => {
    setUnlocked((prev) => {
      const next = [...new Set([...prev, id])];
      localStorage.setItem(UNLOCK_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div data-testid="assignments-page" className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="mb-14 max-w-2xl">
        <div className="text-xs uppercase tracking-[0.22em] text-neutral-500 mb-3">Study Room</div>
        <h1 className="font-serif text-5xl lg:text-6xl text-[#0F172A] tracking-tight leading-[0.95]">
          Student <span className="italic text-[#4A6741]">assignments</span>
        </h1>
        <p className="mt-5 text-neutral-600 text-[15px] leading-relaxed">
          Preview the first page of every assignment free. Purchase to unlock the complete file.
        </p>
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm">Loading assignments...</div>
      ) : items.length === 0 ? (
        <div data-testid="no-assignments" className="rounded-2xl border border-dashed border-neutral-300 p-16 text-center">
          <FileText size={28} className="mx-auto text-neutral-400" />
          <p className="mt-4 text-neutral-500">No assignments uploaded yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((a, i) => (
            <AssignmentCard
              key={a.id}
              a={a}
              i={i}
              price={price}
              unlocked={unlocked.includes(a.id)}
              onUnlocked={markUnlocked}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AssignmentCard({ a, i, price, unlocked, onUnlocked }) {
  const [open, setOpen] = useState(false);
  const fileUrl = `${API}/files/assignment/${a.id}`;
  const isPdf = (a.content_type || "").includes("pdf") || (a.original_filename || "").toLowerCase().endsWith(".pdf");
  const isImage = (a.content_type || "").startsWith("image/");

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: i * 0.05 }}
      data-testid={`assignment-item-${a.id}`}
      className="p-6 rounded-2xl border border-neutral-200 bg-white card-hover"
    >
      <div className="flex items-start gap-4 min-w-0">
        <div className="w-12 h-12 rounded-lg bg-[#F1EDE7] text-[#4A6741] flex items-center justify-center shrink-0">
          <FileText size={20} />
        </div>
        <div className="min-w-0">
          <div className="font-serif text-2xl text-[#0F172A] truncate leading-tight">{a.title}</div>
          <div className="text-xs text-neutral-500 mt-1">
            <span className="uppercase tracking-[0.15em]">{a.subject || "General"}</span>
            {" · "}
            <span className="normal-case tracking-normal">{a.original_filename}</span>
          </div>
        </div>
      </div>

      <div
        data-testid={`assignment-preview-${a.id}`}
        className="relative mt-5 rounded-xl border border-neutral-200 bg-neutral-50 overflow-hidden flex justify-center"
      >
        {isPdf ? (
          <Document
            file={fileUrl}
            loading={<div className="h-72 w-full flex items-center justify-center text-neutral-400 text-sm">Loading preview…</div>}
            error={<PreviewFallback a={a} />}
          >
            <Page pageNumber={1} width={480} renderTextLayer={false} renderAnnotationLayer={false} />
          </Document>
        ) : isImage ? (
          <img src={fileUrl} alt={a.title} loading="lazy" className="w-full object-cover max-h-96" />
        ) : (
          <PreviewFallback a={a} />
        )}

        {!unlocked && (
          <div
            data-testid={`assignment-blur-${a.id}`}
            className="absolute inset-x-0 bottom-0 top-[75%] backdrop-blur-md bg-white/70 border-t border-white/60 flex items-center justify-center"
          >
            <button
              data-testid={`assignment-unlock-btn-${a.id}`}
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#E05A47] text-white text-xs font-medium px-5 h-9 transition-colors"
              style={{ transitionProperty: "background-color" }}
            >
              <Lock size={12}/> Purchase to Unlock
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        {unlocked ? (
          <a
            href={fileUrl}
            target="_blank" rel="noreferrer"
            data-testid={`assignment-download-${a.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#4A6741] hover:bg-[#3a5233] text-white text-xs font-medium px-5 h-9 transition-colors"
            style={{ transitionProperty: "background-color" }}
          >
            <Download size={13}/> Download full file
          </a>
        ) : (
          <span data-testid={`assignment-locked-note-${a.id}`} className="text-xs text-neutral-400 inline-flex items-center gap-1.5">
            <Lock size={12}/> Download locked · ₹{price}
          </span>
        )}
        <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
          {unlocked ? "Unlocked" : "Preview: page 1"}
        </span>
      </div>

      <UnlockDialog a={a} price={price} open={open} setOpen={setOpen} onUnlocked={onUnlocked} />
    </motion.div>
  );
}

function PreviewFallback({ a }) {
  return (
    <div className="h-72 w-full flex flex-col items-center justify-center gap-3 text-neutral-400">
      <FileText size={36}/>
      <div className="text-xs px-6 text-center break-all">{a.original_filename}</div>
    </div>
  );
}

function UnlockDialog({ a, price, open, setOpen, onUnlocked }) {
  const [form, setForm] = useState({ name: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (open) loadRazorpayScript();
  }, [open]);

  const payAndUnlock = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please fill name and phone");
      return;
    }
    setBusy(true);
    const scriptOk = await loadRazorpayScript();
    if (!scriptOk || !window.Razorpay) {
      toast.error("Could not load Razorpay Checkout");
      setBusy(false);
      return;
    }
    let orderRes;
    try {
      const { data } = await api.post("/orders/create-assignment", {
        assignment_id: a.id,
        name: form.name.trim(),
        phone: form.phone.trim(),
      });
      orderRes = data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create order");
      setBusy(false);
      return;
    }

    const rzp = new window.Razorpay({
      key: orderRes.razorpay_key_id,
      amount: orderRes.amount_paise,
      currency: orderRes.currency,
      name: "Chand Book Depot",
      description: orderRes.assignment_title,
      order_id: orderRes.razorpay_order_id,
      prefill: { name: form.name.trim(), contact: form.phone.trim() },
      notes: { internal_order_id: orderRes.order_id },
      theme: { color: "#E05A47" },
      handler: async (rzpRes) => {
        try {
          await api.post("/orders/verify", {
            order_id: orderRes.order_id,
            razorpay_order_id: rzpRes.razorpay_order_id,
            razorpay_payment_id: rzpRes.razorpay_payment_id,
            razorpay_signature: rzpRes.razorpay_signature,
          });
          toast.success("Unlocked! Your download is ready.");
          onUnlocked(a.id);
          setOpen(false);
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
        } finally {
          setBusy(false);
        }
      },
      modal: { ondismiss: () => setBusy(false) },
    });
    rzp.on("payment.failed", (resp) => {
      toast.error(resp?.error?.description || "Payment failed");
      setBusy(false);
    });
    rzp.open();
  };

  const unlockWithPhone = async () => {
    if (!form.phone.trim()) {
      toast.error("Enter the phone number used at purchase");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.post(`/assignments/${a.id}/access`, { phone: form.phone.trim() });
      if (data.unlocked) {
        toast.success("Purchase verified. Unlocked!");
        onUnlocked(a.id);
        setOpen(false);
      } else {
        toast.error("No confirmed order found for this phone number");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not verify right now");
    } finally {
      setChecking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm" data-testid={`unlock-dialog-${a.id}`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Unlock this assignment</DialogTitle>
          <DialogDescription className="text-xs">
            {a.title} · one-time purchase · ₹{price}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Your name</Label>
            <Input data-testid={`unlock-name-input-${a.id}`} className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}/>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Phone</Label>
            <Input data-testid={`unlock-phone-input-${a.id}`} className="mt-1.5" placeholder="10-digit mobile" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}/>
          </div>
          <Button
            data-testid={`unlock-pay-btn-${a.id}`}
            onClick={payAndUnlock}
            disabled={busy || checking}
            className="w-full h-11 rounded-full bg-[#E05A47] hover:bg-[#c94a38] text-white transition-colors"
            style={{ transitionProperty: "background-color" }}
          >
            {busy ? <><Loader2 size={14} className="mr-2 animate-spin"/>Opening…</> : `Pay ₹${price} & Unlock`}
          </Button>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-neutral-400">
            <span className="flex-1 h-px bg-neutral-200"/> or <span className="flex-1 h-px bg-neutral-200"/>
          </div>
          <Button
            variant="outline"
            data-testid={`unlock-phone-btn-${a.id}`}
            onClick={unlockWithPhone}
            disabled={busy || checking}
            className="w-full h-11 rounded-full"
          >
            {checking ? <><Loader2 size={14} className="mr-2 animate-spin"/>Checking…</> : "Already purchased? Unlock with phone"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
